'use client';

import React, {createContext, ReactNode, useContext, useMemo} from 'react';
import {
    SubscriptionPlanContext,
    SubscriptionPlansContext,
    ConsumerSubscriptionPlan,
    ConsumerSubscriptionValuePlan,
    ConsumerSubscriptionGrowWithGoogle,
    ConsumerSubscriptionGwGPlusPersonalPlan,
    SubscriptionPlanProductType,
    useCourseSubscriptionPlan,
    useConsumerSubscriptionPlan,
} from '@udemy-v2/subscription-graphql';
import {
    useCommonData,
    getActiveConsumerSubscription,
    CommonDataSubscription,
} from '@udemy-v2/common-data';
import {useFeatures} from '@udemy-v2/react-experimentation';

export type ClpPurchaseBoxSubscriptionPlan =
    | ConsumerSubscriptionPlan
    | ConsumerSubscriptionValuePlan
    | ConsumerSubscriptionGwGPlusPersonalPlan;

export type ClpSubscriptionPlan =
    | ClpPurchaseBoxSubscriptionPlan
    | ConsumerSubscriptionGrowWithGoogle;

/**
 * Context to manage the increasingly complex subscription plan checks in the CLP
 * This context is used to abstract subscription related context from the CLPPurchaseContextProvider
 * and also enable us to create custom hooks to access specific subscription-related statuses.
 */
export interface ClpSubscriptionContext {
    subscriptionPlan?: SubscriptionPlanContext<ClpSubscriptionPlan>;
    unwrappedConsumerSubscriptionPlan?: ClpPurchaseBoxSubscriptionPlan;
    unwrappedPersonalPlan?: ConsumerSubscriptionPlan;
    subscribedConsumerSubscriptionPlan?: CommonDataSubscription;
    isPersonalPlanSubscriber: boolean;
    isCourseIncludedInUserSubscription: boolean;
    isCourseInAvailablePlans: boolean;
    isCourseInPersonalPlanCatalog: boolean;
    isPersonalPlanLoading: boolean;
    isSubscriptionPlanLoading: boolean;
    isCourseInValuePlanCatalog: boolean;
    isCourseInAllPlans: boolean;
    isCourseInSelectPlansOnly: boolean;
    isValuePlanSubscriber: boolean;
    subscribedProductType?: SubscriptionPlanProductType;
}

// Exported so story files can provide mock values via ReactClpSubscriptionContext.Provider
export const ReactClpSubscriptionContext = createContext<ClpSubscriptionContext | undefined>(undefined);

export interface ClpSubscriptionContextProviderProps {
    children: ReactNode;
    courseId: string;
}

// TODO: Move this into a service. At the moment the concept of 'tiers' or 'upgrade paths' is not modeled anywhere except here,
// which is hugely unmaintainable.
const PURCHASEABLE_PLAN_TYPES_ORDERED_BY_TIER_ASCENDING: SubscriptionPlanProductType[] = [
    SubscriptionPlanProductType.ConsumersubscriptionValuePlan,
    SubscriptionPlanProductType.Consumersubscription,
    SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
];

// A helper function meant to consume an entire SubscriptionPlansContext, and return the lowest tier plan
// that can be purchased via the CLP.
const getLowestTierPurchaseablePlan = (
    plans: SubscriptionPlansContext,
    subscriptionTiersEnabled: boolean = false,
): SubscriptionPlanContext<ClpPurchaseBoxSubscriptionPlan> | undefined => {
    let planToReturn = undefined;

    // Iterate through the tiers backwards, so lower tiers override higher tiers.
    for (const planType of PURCHASEABLE_PLAN_TYPES_ORDERED_BY_TIER_ASCENDING.toReversed()) {
        if (
            !subscriptionTiersEnabled &&
            planType === SubscriptionPlanProductType.ConsumersubscriptionValuePlan
        ) {
            continue;
        }
        const plan = plans[planType];
        if (plan !== undefined) {
            planToReturn = plan as ClpPurchaseBoxSubscriptionPlan;
        }
    }
    return planToReturn;
};

/** Returns true if a plan context value represents an available (resolved, non-loading) plan. */
const isPlanAvailable = (plan: SubscriptionPlanContext<ClpSubscriptionPlan> | undefined): boolean =>
    plan !== undefined && 'id' in plan && !('isLoading' in plan && plan.isLoading);

/** Returns true if a plan context value is still in its loading state */
const isPlanLoading = (plan: SubscriptionPlanContext<ClpSubscriptionPlan> | undefined): boolean =>
    !!(plan && 'isLoading' in plan && plan.isLoading);

/**
 * Provider that uses the course-specific subscription context to centralize logic that determines
 * subscription plan availability, user subscription status, and course inclusion in subscription plans. This provider computes subscription-related
 * statuses once and memoizes them, providing a single source of truth for subscription-related statuses in the CLP.
 * This enables us to more easily test a single block of business logic and avoid re-computing values on each re-render.
 */
export const ClpSubscriptionContextProvider: React.FC<ClpSubscriptionContextProviderProps> = ({
    children,
    courseId,
}) => {
    const commonData = useCommonData();
    const {featureVariantAssignments} = useFeatures(['value_planstarter_plan']);
    const subscriptionTiersEnabled = (featureVariantAssignments.get('value_planstarter_plan')
        ?.configuration?.enableValuePlan ?? false) as boolean;
    const subscribedConsumerSubscriptionPlan = getActiveConsumerSubscription(commonData, true);
    const subscribedProductType = subscribedConsumerSubscriptionPlan?.subscribedPlan
        ?.productType as SubscriptionPlanProductType | undefined;

    // Fetch course-scoped plan data for each plan type.
    // After the CourseSubscriptionsQuery resolves, unavailable plans return undefined (not a loading sentinel).
    const consumerSubscriptionPlan = useCourseSubscriptionPlan(
        courseId,
        SubscriptionPlanProductType.Consumersubscription,
    ) as SubscriptionPlanContext<ConsumerSubscriptionPlan> | undefined;
    const valueSubscriptionPlan = useCourseSubscriptionPlan(
        courseId,
        SubscriptionPlanProductType.ConsumersubscriptionValuePlan,
    ) as SubscriptionPlanContext<ConsumerSubscriptionValuePlan> | undefined;
    const gwgPlusPersonalPlan = useCourseSubscriptionPlan(
        courseId,
        SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
    ) as SubscriptionPlanContext<ConsumerSubscriptionGwGPlusPersonalPlan> | undefined;
    const gwgSubscriptionPlan = useCourseSubscriptionPlan(
        courseId,
        SubscriptionPlanProductType.GrowWithGoogle,
    ) as SubscriptionPlanContext<ConsumerSubscriptionGrowWithGoogle> | undefined;

    const personalPlan = useConsumerSubscriptionPlan();

    const value = useMemo(() => {
        const plansByProductType: Partial<
            Record<
                SubscriptionPlanProductType,
                SubscriptionPlanContext<ClpSubscriptionPlan> | undefined
            >
        > = {
            [SubscriptionPlanProductType.Consumersubscription]: consumerSubscriptionPlan,
            [SubscriptionPlanProductType.ConsumersubscriptionValuePlan]: valueSubscriptionPlan,
            [SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle]:
                gwgPlusPersonalPlan,
            [SubscriptionPlanProductType.GrowWithGoogle]: gwgSubscriptionPlan,
        };
        const allPlans = Object.values(plansByProductType);

        const plans = {
            [SubscriptionPlanProductType.ConsumersubscriptionValuePlan]: valueSubscriptionPlan,
            [SubscriptionPlanProductType.Consumersubscription]: consumerSubscriptionPlan,
            [SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle]:
                gwgPlusPersonalPlan,
        } as SubscriptionPlansContext;
        const purchaseBoxSubscriptionPlan = getLowestTierPurchaseablePlan(
            plans,
            subscriptionTiersEnabled,
        );
        const subscriptionPlan = purchaseBoxSubscriptionPlan ?? gwgSubscriptionPlan;

        const unwrappedConsumerSubscriptionPlan =
            purchaseBoxSubscriptionPlan && 'id' in purchaseBoxSubscriptionPlan
                ? purchaseBoxSubscriptionPlan
                : undefined;

        const unwrappedPersonalPlan =
            personalPlan && 'id' in personalPlan
                ? (personalPlan as ConsumerSubscriptionPlan)
                : undefined;

        const isCourseInPersonalPlanCatalog = isPlanAvailable(consumerSubscriptionPlan);
        const isCourseInValuePlanCatalog = isPlanAvailable(valueSubscriptionPlan);
        const isCourseInAvailablePlans = allPlans.some(isPlanAvailable);
        const isCourseInAllPlans = isCourseInValuePlanCatalog && isCourseInPersonalPlanCatalog;
        const isCourseInSelectPlansOnly =
            isCourseInPersonalPlanCatalog && !isCourseInValuePlanCatalog;

        const isSubscriptionPlanLoading = !!(
            subscriptionPlan &&
            'isLoading' in subscriptionPlan &&
            subscriptionPlan.isLoading
        );
        const isPersonalPlanLoading = isPlanLoading(consumerSubscriptionPlan);

        // Determine if user is a Personal Plan subscriber - this uses commonData which is populated on initial page load
        // To determine if the specific course is included in their plan, we need to use the isCourseIncludedInUserSubscription field below
        const isPersonalPlanSubscriber = !!subscribedConsumerSubscriptionPlan;

        const isValuePlanSubscriber =
            subscribedProductType === SubscriptionPlanProductType.ConsumersubscriptionValuePlan;

        // Resolve the course-scoped plan for the user's subscribed product type.
        // This includes GwG and GwG+PP so that cross-plan subscribers (e.g. a GwG+PP user
        // viewing a PP-only course) are correctly handled.
        const subscriptionPlanForProductType = subscribedProductType
            ? plansByProductType[subscribedProductType]
            : undefined;

        // Course is included in the user's subscription when the course-scoped hook
        // for their subscribed plan type returns the isSubscriber state.
        const isCourseIncludedInUserSubscription = !!(
            subscribedProductType &&
            subscriptionPlanForProductType &&
            'isSubscriber' in subscriptionPlanForProductType &&
            subscriptionPlanForProductType.isSubscriber
        );

        return {
            subscriptionPlan,
            unwrappedConsumerSubscriptionPlan,
            unwrappedPersonalPlan,
            subscribedConsumerSubscriptionPlan: subscribedConsumerSubscriptionPlan ?? undefined,
            isPersonalPlanSubscriber,
            isCourseIncludedInUserSubscription,
            isCourseInAvailablePlans,
            isCourseInPersonalPlanCatalog,
            isPersonalPlanLoading,
            isSubscriptionPlanLoading,
            isCourseInValuePlanCatalog,
            isCourseInAllPlans,
            isCourseInSelectPlansOnly,
            isValuePlanSubscriber,
            subscribedProductType,
        };
    }, [
        consumerSubscriptionPlan,
        valueSubscriptionPlan,
        gwgPlusPersonalPlan,
        gwgSubscriptionPlan,
        personalPlan,
        subscribedConsumerSubscriptionPlan,
        subscribedProductType,
        subscriptionTiersEnabled,
    ]);

    return (
        <ReactClpSubscriptionContext.Provider value={value}>
            {children}
        </ReactClpSubscriptionContext.Provider>
    );
};

/**
 * Hook to access the CLP subscription context
 * @returns ClpSubscriptionContext
 * @throws Error if used outside of ClpSubscriptionContextProvider
 */
export function useClpSubscriptionContext(): ClpSubscriptionContext {
    const context = useContext(ReactClpSubscriptionContext);

    if (context === undefined) {
        throw new Error(
            'useClpSubscriptionContext must be used within a ClpSubscriptionContextProvider',
        );
    }

    return context;
}
